import {
  CrewClaimResolutionMethodEnum,
  crewClaimResolutionMethodEnumName,
} from 'corso-types';

import { Badge, OrderLineItem } from 'corso-admin-ui';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import { useCurrency } from '~/hooks/useCurrency';
import { formatter } from 'corso-web-lib';
import { GiftCardLineItem } from '~/components/lineItems/GiftCardLineItem';
import { RefundLineItem } from '~/components/lineItems/RefundLineItem';

const calculateOutstandingAmount = (
  claim: ReturnType<typeof useClaim>,
  claimLineItem: ReturnType<typeof useClaim>['claimLineItems'][number],
) => {
  const amount =
    // if finalized, use the amount, otherwise use the estimated amount
    claimLineItem.finalizedOn
      ? claimLineItem.amount
      : claim.requestSummary?.approvedLineItems.find(
          (lineItem) => lineItem.id === claimLineItem.id,
        )?.estimatedAmount;

  const { variantExchangeAmountResolutionMethodEnum: outstandingResolution } =
    claimLineItem;

  if (amount === undefined) return null;

  if ((amount && amount <= 0) || !outstandingResolution) return null;

  return {
    amount,
    outstandingResolution,
  };
};

export const wasLineItemZeroedThroughDeduction = (
  claim: ReturnType<typeof useClaim>,
  claimLineItem: ReturnType<typeof useClaim>['claimLineItems'][number],
) => {
  if (claim.finalizedOn) return false;

  const requestSummaryLineItem = claim.requestSummary?.approvedLineItems.find(
    (lineItem) => lineItem.id === claimLineItem.id,
  );

  return requestSummaryLineItem?.wasZeroedThroughDeduction ?? false;
};

function ExchangeOutstandingAmountLineItem({
  claimLineItemId,
  outstandingResolution,
  amount,
}: {
  claimLineItemId: number;
  outstandingResolution: CrewClaimResolutionMethodEnum;
  amount: number;
}) {
  if (outstandingResolution === CrewClaimResolutionMethodEnum.giftCard) {
    return (
      <GiftCardLineItem
        giftCard={{
          id: `outstanding-gift-card-${claimLineItemId}`,
          amount,
          wasResolvedExternally: false,
        }}
      />
    );
  }

  return (
    <RefundLineItem
      refund={{
        id: `outstanding-refund-${claimLineItemId}`,
        amount,
        wasResolvedExternally: false,
      }}
    />
  );
}

export function UpsellBadge({
  claimLineItem,
}: {
  claimLineItem: ReturnType<typeof useClaim>['claimLineItems'][number];
}) {
  const { exchangeDeltaAmount, resolutionMethodEnum } = claimLineItem;

  const { currencyCode } = useCurrency();

  if (!exchangeDeltaAmount || exchangeDeltaAmount === 0) return null;

  const isUpsell = exchangeDeltaAmount > 0;

  if (!isUpsell || resolutionMethodEnum !== 'Variant_Exchange') return null;

  return (
    <Badge variant="success">
      {`${formatter.currency(
        Math.abs(exchangeDeltaAmount),
        currencyCode,
      )}  Upsell`}
    </Badge>
  );
}

export function ExchangeForDetail({
  claimLineItem,
}: {
  claimLineItem: ReturnType<typeof useClaim>['claimLineItems'][number];
}) {
  const claim = useClaim();
  const { currencyCode } = useCurrency();

  const { taxRate } = claim.originalStoreOrder;

  const upsellResolutions: CrewClaimResolutionMethodEnum[] = [
    CrewClaimResolutionMethodEnum.replacementOrder,
    CrewClaimResolutionMethodEnum.variantExchange,
  ];

  // we may need to support this when moving from existing claims that will not have the actual resolution method set until the claim is finalized
  const resolutionMethod =
    claimLineItem.resolutionMethodEnum ??
    claimLineItem.requestedResolutionMethodEnum;

  if (!upsellResolutions.includes(resolutionMethod)) {
    return null;
  }

  const { variantExchangeLineItem } = claimLineItem;

  const outstandingAmount = calculateOutstandingAmount(claim, claimLineItem);

  if (!variantExchangeLineItem && !outstandingAmount) return null;

  return (
    <section className="flex flex-col gap-4">
      <h6 className="text-xs font-semibold text-neutral-500">Exchange For</h6>

      {variantExchangeLineItem && (
        <OrderLineItem
          size="sm"
          sku={variantExchangeLineItem.sku}
          title={variantExchangeLineItem.title}
          src={variantExchangeLineItem.imgUrl}
          options={variantExchangeLineItem.options}
          quantity={variantExchangeLineItem.quantity}
          price={{
            currencyCode,
            unitPrice: variantExchangeLineItem.price,
            unitTax: taxRate ? variantExchangeLineItem.price * taxRate : null,
          }}
        />
      )}
      {outstandingAmount && (
        <ExchangeOutstandingAmountLineItem
          claimLineItemId={claimLineItem.id}
          outstandingResolution={outstandingAmount.outstandingResolution}
          amount={outstandingAmount.amount}
        />
      )}
    </section>
  );
}

export const computeDisplayResolutionMethod = (
  claimLineItem: ReturnType<typeof useClaim>['claimLineItems'][number],
  wasZeroedThroughDeduction: boolean,
) => {
  const resolution =
    // wasZeroedThroughDeduction only applies to claims before they are finalized indicating a claim line item who's value was applied to a replacement order
    wasZeroedThroughDeduction
      ? 'Other'
      : (claimLineItem.resolutionMethodEnum ??
        claimLineItem.requestedResolutionMethodEnum);

  return crewClaimResolutionMethodEnumName[resolution];
};
